import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { modalityPaymentCategory } from "@/components/app/ModalityBadge";
import { editionLabel } from "@/components/app/EditionSelect";

const KEY = "athena_cart_v1";
const CartCtx = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [addedCourse, setAddedCourse] = useState(null); // trigger per il popup "Aggiunto al carrello"

  // Persist on change
  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(items)); } catch { /* quota */ }
  }, [items]);

  const addItem = (course, quantity = 1, edition = null) => {
    // Rule: non è possibile mescolare corsi E-learning con corsi in Aula/Videoconferenza.
    const newCat = modalityPaymentCategory(course?.modality);
    const existingCat = (() => {
      const cats = new Set();
      items.forEach((it) => {
        const c = modalityPaymentCategory(it.modality);
        if (c !== "unknown") cats.add(c);
      });
      return cats.size === 1 ? [...cats][0] : null;
    })();
    if (newCat !== "unknown" && existingCat && existingCat !== newCat) {
      toast.error(
        existingCat === "elearning"
          ? "Il carrello contiene già corsi E-learning. Completa quell'ordine prima di aggiungere corsi in Aula/Videoconferenza."
          : "Il carrello contiene già corsi in Aula/Videoconferenza. Completa quell'ordine prima di aggiungere corsi E-learning."
      );
      return false;
    }

    const inCart = items.find((i) => i.course_id === course.id);
    if (inCart && (inCart.edition_id || null) !== (edition?.id || null)) {
      toast.error("Questo corso è già nel carrello con un'altra edizione: rimuovilo dal carrello per cambiare edizione.");
      return false;
    }
    const price = (typeof course.partner_price_final === "number" && course.partner_price_final > 0)
      ? course.partner_price_final : (course.partner_price || 0);
    setItems((prev) => {
      const existing = prev.find((i) => i.course_id === course.id);
      if (existing) {
        return prev.map((i) => i.course_id === course.id ? { ...i, quantity: i.quantity + quantity } : i);
      }
      return [...prev, {
        course_id: course.id,
        title: course.title,
        image_url: course.image_url,
        unit_price: price,
        vat_rate: typeof course.vat_rate === "number" ? course.vat_rate : 22,
        quantity,
        modality: course.modality,
        cfp_enabled: !!course.cfp_enabled,
        cfp_profiles: course.cfp_profiles || [],
        edition_id: edition?.id || null,
        edition_label: edition ? editionLabel(edition) : "",
      }];
    });
    setAddedCourse({ id: course.id, title: course.title, modality: course.modality, at: Date.now() });
    return true;
  };

  const updateQty = (course_id, qty) => setItems((prev) => prev.map((i) => i.course_id === course_id ? { ...i, quantity: Math.max(1, qty) } : i));
  const removeItem = (course_id) => setItems((prev) => prev.filter((i) => i.course_id !== course_id));
  const clear = () => setItems([]);
  const clearAdded = () => setAddedCourse(null);

  const total = useMemo(() => items.reduce((s, i) => s + i.unit_price * i.quantity, 0), [items]);
  const totalQty = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  return <CartCtx.Provider value={{ items, totalQty, addItem, updateQty, removeItem, clear, total, addedCourse, clearAdded }}>{children}</CartCtx.Provider>;
}

export function useCart() { return useContext(CartCtx); }
